import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { diceRollerDefinition } from './diceRollerDefinition';
import {
  readSelectedRollMode,
  writeSelectedRollMode,
} from './diceRollerStorage';
import './DiceRollerGame.css';

export const DEFAULT_DICE_ROLLER_MODE_ID = 'd20';
export const ROLL_FRAME_MS = 90;
export const ROLL_ANIMATION_MS = 720;

function getRollModeById(definition, modeId) {
  return definition.modes.find((mode) => mode.id === modeId) ?? null;
}

function getModePlaceholder() {
  return '—';
}

export function normalizeDiceRollerMode(modeId, definition = diceRollerDefinition) {
  return getRollModeById(definition, modeId)?.id ?? DEFAULT_DICE_ROLLER_MODE_ID;
}

export function getAnimatedResult(mode, frameIndex) {
  if (mode.kind === 'coin') {
    return frameIndex % 2 === 0 ? 'Heads' : 'Tails';
  }

  return String((frameIndex % mode.sides) + 1);
}

export function rollSelectedMode(mode) {
  if (mode.kind === 'coin') {
    return Math.random() < 0.5 ? 'Heads' : 'Tails';
  }

  return String(Math.floor(Math.random() * mode.sides) + 1);
}

function extractDiceBoxValue(results) {
  if (!Array.isArray(results) || results.length === 0) {
    return null;
  }

  const firstGroup = results[0];
  const firstValue = firstGroup?.rolls?.[0]?.value ?? firstGroup?.value;

  if (typeof firstValue !== 'number' || Number.isNaN(firstValue)) {
    return null;
  }

  return firstValue;
}

function DiceRollerGame({
  definition = diceRollerDefinition,
  onReturnToLibrary,
}) {
  const [selectedModeId, setSelectedModeId] = useState(() =>
    normalizeDiceRollerMode(
      readSelectedRollMode(window.localStorage, definition.storageNamespace),
      definition
    )
  );
  const selectedMode = useMemo(
    () => getRollModeById(definition, selectedModeId) ?? definition.modes[0],
    [definition, selectedModeId]
  );
  const [displayResult, setDisplayResult] = useState(() => getModePlaceholder(selectedMode));
  const [lastResult, setLastResult] = useState(null);
  const [isRolling, setIsRolling] = useState(false);
  const [isModeMenuOpen, setIsModeMenuOpen] = useState(false);
  const [diceBoxStatus, setDiceBoxStatus] = useState(() => (
    process.env.NODE_ENV === 'test' ? 'disabled' : 'loading'
  ));
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const diceBoxRef = useRef(null);
  const diceBoxContainerIdRef = useRef(`dice-roller-box-${definition.id}`);
  const isMountedRef = useRef(true);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  const clearAnimation = useCallback(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const resetDisplay = useCallback((mode) => {
    setDisplayResult(getModePlaceholder(mode));
    setLastResult(null);
    setIsRolling(false);
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return undefined;
    }

    let isDisposed = false;

    const initDiceBox = async () => {
      try {
        const { default: DiceBox } = await import('@3d-dice/dice-box');
        const diceBox = new DiceBox(`#${diceBoxContainerIdRef.current}`, {
          assetPath: `${process.env.PUBLIC_URL || ''}/assets/dice-box/`,
          offscreen: true,
        });

        await diceBox.init();

        if (isDisposed) {
          return;
        }

        diceBoxRef.current = diceBox;
        setDiceBoxStatus('ready');
      } catch (error) {
        if (!isDisposed) {
          setDiceBoxStatus('error');
        }
      }
    };

    initDiceBox();

    return () => {
      isDisposed = true;
      diceBoxRef.current?.clear();
      diceBoxRef.current = null;
    };
  }, [definition.id]);

  const selectMode = useCallback((modeId) => {
    const normalizedModeId = normalizeDiceRollerMode(modeId, definition);
    const nextMode = getRollModeById(definition, normalizedModeId) ?? definition.modes[0];

    clearAnimation();
    setSelectedModeId(normalizedModeId);
    writeSelectedRollMode(normalizedModeId, window.localStorage, definition.storageNamespace);
    resetDisplay(nextMode);
    setIsModeMenuOpen(false);
  }, [clearAnimation, definition, resetDisplay]);

  const rollMode = useCallback(() => {
    if (isRolling) {
      return;
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (diceBoxStatus === 'ready' && !prefersReducedMotion && diceBoxRef.current) {
      const notation = selectedMode.kind === 'coin' ? '1d2' : `1${selectedMode.id}`;

      clearAnimation();
      setIsRolling(true);
      setDisplayResult(selectedMode.kind === 'coin' ? 'Flipping…' : 'Rolling…');
      diceBoxRef.current.roll(notation, { newStartPoint: true })
        .then((results) => {
          if (!isMountedRef.current) {
            return;
          }

          const rolledValue = extractDiceBoxValue(results);
          const finalResult = selectedMode.kind === 'coin'
            ? (rolledValue === 2 ? 'Tails' : 'Heads')
            : String(rolledValue ?? rollSelectedMode(selectedMode));

          setDisplayResult(finalResult);
          setLastResult(finalResult);
          setIsRolling(false);
        })
        .catch(() => {
          if (!isMountedRef.current) {
            return;
          }

          const fallbackResult = rollSelectedMode(selectedMode);

          setDisplayResult(fallbackResult);
          setLastResult(fallbackResult);
          setDiceBoxStatus('error');
          setIsRolling(false);
        });
      return;
    }

    const nextResult = rollSelectedMode(selectedMode);
    clearAnimation();
    setIsRolling(true);
    setDisplayResult(getAnimatedResult(selectedMode, 1));

    if (prefersReducedMotion) {
      setDisplayResult(nextResult);
      setLastResult(nextResult);
      setIsRolling(false);
      return;
    }

    let frameIndex = 0;
    intervalRef.current = window.setInterval(() => {
      frameIndex += 1;
      setDisplayResult(getAnimatedResult(selectedMode, frameIndex));
    }, ROLL_FRAME_MS);

    timeoutRef.current = window.setTimeout(() => {
      clearAnimation();
      setDisplayResult(nextResult);
      setLastResult(nextResult);
      setIsRolling(false);
    }, ROLL_ANIMATION_MS);
  }, [clearAnimation, diceBoxStatus, isRolling, selectedMode]);

  useEffect(() => () => {
    clearAnimation();
  }, [clearAnimation]);

  useEffect(() => {
    const modeIndexById = definition.modes.reduce((memo, mode, index) => ({
      ...memo,
      [String(index + 1)]: mode.id,
    }), {});

    const onKeyDown = (event) => {
      if (event.target instanceof HTMLElement && event.target.closest('button')) {
        return;
      }

      if (event.key === 'Escape') {
        if (isModeMenuOpen) {
          setIsModeMenuOpen(false);
          return;
        }
        onReturnToLibrary?.();
        return;
      }

      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        rollMode();
        return;
      }

      const modeId = modeIndexById[event.key];
      if (modeId) {
        event.preventDefault();
        selectMode(modeId);
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [definition.modes, isModeMenuOpen, onReturnToLibrary, rollMode, selectMode]);

  const statusLabel = isRolling
    ? (selectedMode.kind === 'coin' ? 'Flipping…' : `Rolling ${selectedMode.label}…`)
    : (lastResult ? `Last result: ${lastResult}` : (selectedMode.kind === 'coin' ? 'Tap panel to flip' : 'Tap panel to roll'));
  const isCoinMode = selectedMode.kind === 'coin';
  const coinFaceGlyph = displayResult === 'Tails' ? '◑' : '◐';
  const stageClassName = `dice-roller-stage${isRolling ? ` ${selectedMode.kind === 'coin' ? 'is-flipping' : 'is-rolling'}` : ''}${diceBoxStatus === 'ready' ? ' has-dicebox' : ''}`;

  return (
    <main className="game-app dice-roller-app">
      <section className="dice-roller-shell" aria-label="Dice Roller">
        <header className="dice-roller-topbar">
          <div className="dice-roller-menu">
            <button
              className="dice-roller-menu-button"
              type="button"
              onClick={() => setIsModeMenuOpen((current) => !current)}
              aria-expanded={isModeMenuOpen}
              aria-controls="dice-roller-menu-panel"
            >
              <span>Menu · {selectedMode.label}</span>
              <span aria-hidden="true">{isModeMenuOpen ? '▴' : '▾'}</span>
            </button>
            {isModeMenuOpen ? (
              <div className="dice-roller-menu-panel" id="dice-roller-menu-panel">
                <button className="dice-roller-menu-option" type="button" onClick={onReturnToLibrary}>
                  Library
                </button>
                <div className="dice-roller-mode-grid" aria-label="Dice and coin options">
                  {definition.modes.map((mode) => {
                    const isSelected = mode.id === selectedMode.id;

                    return (
                      <button
                        key={mode.id}
                        type="button"
                        className={`dice-roller-mode-button${isSelected ? ' selected' : ''}`}
                        aria-label={`Select ${mode.label}`}
                        aria-pressed={isSelected}
                        onClick={() => selectMode(mode.id)}
                      >
                        <span className="dice-roller-mode-badge">{mode.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
          <div className="dice-roller-brand">
            <h1>Dice Roller</h1>
          </div>
        </header>

        <div className="dice-roller-board">
          <section className="dice-roller-result-panel" aria-label="Roll result panel">
            <div className="dice-roller-stage-copy">
              <p className="dice-roller-stage-status">{statusLabel}</p>
            </div>
            <div
              className={stageClassName}
              aria-live="polite"
              aria-label="Latest result"
              role="button"
              tabIndex={0}
              onClick={rollMode}
              onKeyDown={(event) => {
                if (event.key === ' ' || event.key === 'Enter') {
                  event.preventDefault();
                  rollMode();
                }
              }}
            >
              <div className="dice-roller-stage-canvas" id={diceBoxContainerIdRef.current} aria-hidden="true" />
              {isCoinMode ? (
                <div className="dice-roller-coin" data-testid="dice-roller-coin" aria-hidden="true">
                  <span>{coinFaceGlyph}</span>
                </div>
              ) : (
                <span className="dice-roller-stage-shape" aria-hidden="true">✦</span>
              )}
              <strong className="dice-roller-stage-value">{displayResult}</strong>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}

export default DiceRollerGame;
